import React, { useState, useMemo } from "react";
import api from "../../utils/api";
import IconButton from "@mui/material/IconButton";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import FilterListIcon from "@mui/icons-material/FilterList";
import PageHeader from "../common/PageHeader";
import OrderFilters from "./components/OrderFilters";
import Pagination from "../Pagination";
import { useRestaurantConfig } from "../../utils/restaurantConfig";
import { getCurrencySymbol } from "../../helper/formatCurrency";
import { useProductReport } from "./hooks/useProductReport";
import { formatDateForAPI } from "./utils/orderUtils";
import { TABLE_ROWS_PER_PAGE } from "./utils/constants";
import { downloadProductReportPDF } from "./utils/pdfUtils";
import { normalizeProductReportData } from "./utils/productReportUtils";
import OrderActions from "./components/OrderActions";

const ProductReport = () => {
  const { locale, timezone, restaurantName } = useRestaurantConfig();
  const currencySymbol = getCurrencySymbol(locale);

  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState("totalSales");
  const [sortOrder, setSortOrder] = useState(-1);
  const [status, setStatus] = useState("");
  const [orderType, setOrderType] = useState("");
  const [search, setSearch] = useState("");
  const [qrFeatureEnabled, setQrFeatureEnabled] = useState(true);

  React.useEffect(() => {
    const loadFeatures = async () => {
      try {
        const res = await api.get("/settings/features");
        if (res?.data?.success && res?.data?.features) {
          setQrFeatureEnabled(res.data.features.qrFeature !== false);
        }
      } catch {
        setQrFeatureEnabled(true);
      }
    };

    loadFeatures();
  }, []);

  const {
    products,
    totalProducts,
    totalQty,
    totalSales,
    fromDate,
    setFromDate,
    toDate,
    setToDate,
    showAllDates,
    setShowAllDates,
    isLoading,
  } = useProductReport(qrFeatureEnabled);

  const {
    products: reportProducts,
    totalProducts: reportTotalProducts,
    totalQty: reportTotalQty,
    totalSales: reportTotalSales,
  } = useMemo(
    () =>
      normalizeProductReportData(
        products,
        { totalProducts, totalQty, totalSales },
        qrFeatureEnabled
      ),
    [products, totalProducts, totalQty, totalSales, qrFeatureEnabled]
  );

  const handleDownloadPDF = () => {
    const dateRangeStr = showAllDates
      ? "All Dates"
      : `${(fromDate && formatDateForAPI(fromDate)) || "N/A"} - ${(toDate && formatDateForAPI(toDate)) || "N/A"}`;

    downloadProductReportPDF(
      reportProducts,
      currencySymbol,
      timezone,
      restaurantName,
      dateRangeStr,
      reportTotalProducts,
      reportTotalQty,
      reportTotalSales,
      qrFeatureEnabled
    );
  };

  const sortedProducts = [...reportProducts].sort((a, b) => {
    const numericFields = [
      "dineInQty",
      ...(qrFeatureEnabled ? ["qrQty"] : []),
      "takeawayQty",
      "onlineQty",
      "totalQty",
      "totalSales",
    ];
    const aValue = numericFields.includes(sortField)
      ? Number(a[sortField] || 0)
      : a[sortField];
    const bValue = numericFields.includes(sortField)
      ? Number(b[sortField] || 0)
      : b[sortField];

    const aStr = aValue ?? "";
    const bStr = bValue ?? "";
    if (aStr < bStr) return -1 * sortOrder;
    if (aStr > bStr) return 1 * sortOrder;
    return 0;
  });

  const indexOfLast = currentPage * TABLE_ROWS_PER_PAGE;
  const indexOfFirst = indexOfLast - TABLE_ROWS_PER_PAGE;
  const currentRows = sortedProducts.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(reportProducts.length / TABLE_ROWS_PER_PAGE);

  React.useEffect(() => {
    setCurrentPage(1);
  }, [reportProducts.length]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder * -1);
    } else {
      setSortField(field);
      setSortOrder(field === "productName" ? 1 : -1);
    }
  };

  const SortIndicator = ({ field }) =>
    sortField === field ? (
      <span className="text-xs">{sortOrder === 1 ? "▲" : "▼"}</span>
    ) : null;

  const thClass =
    "px-6 py-5 text-left text-sm font-bold text-white uppercase tracking-wider cursor-pointer hover:bg-gray-800";

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 px-2 py-3">
      <PageHeader
        title="Product Report"
        subtitle={
          <span className="flex items-center gap-2">
            <span className="inline-block w-2 h-2 bg-blue-500 rounded-full"></span>
            View product order activity and sales breakdown
          </span>
        }
      />

      {/* Stats Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        {/* Total Products Card */}
        <div className="bg-gradient-to-br from-emerald-500 to-green-600 rounded-lg p-3 shadow-md hover:shadow-lg transition-all">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-md flex items-center justify-center">
              <svg
                className="w-4 h-4 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"
                />
              </svg>
            </div>
            <div className="text-white/90 text-xs font-medium">
              Total Products
            </div>
          </div>
          <div className="text-white text-lg lg:text-xl font-bold tracking-tight">
            {isLoading ? (
              <span className="animate-pulse">...</span>
            ) : (
              reportTotalProducts
            )}
          </div>
        </div>

        {/* Total Quantity Card */}
        <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg p-3 shadow-md hover:shadow-lg transition-all">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-md flex items-center justify-center">
              <svg
                className="w-4 h-4 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
            </div>
            <div className="text-white/90 text-xs font-medium">
              Total Items Sold
            </div>
          </div>
          <div className="text-white text-lg lg:text-xl font-bold tracking-tight">
            {isLoading ? (
              <span className="animate-pulse">...</span>
            ) : (
              reportTotalQty
            )}
          </div>
        </div>

        {/* Total Sales Card */}
        <div className="bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg p-3 shadow-md hover:shadow-lg transition-all">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-md flex items-center justify-center">
              <svg
                className="w-4 h-4 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div className="text-white/90 text-xs font-medium">
              Total Product Sales
            </div>
          </div>
          <div className="text-white text-lg lg:text-xl font-bold tracking-tight">
            {isLoading ? (
              <span className="animate-pulse">...</span>
            ) : (
              <>{currencySymbol}{Number(reportTotalSales || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</>
            )}
          </div>
        </div>

        {/* Date Range Card */}
        <div className="bg-gradient-to-br from-orange-500 to-red-600 rounded-lg p-3 shadow-md hover:shadow-lg transition-all">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-md flex items-center justify-center">
              <svg
                className="w-4 h-4 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            </div>
            <div className="text-white/90 text-xs font-medium">Date Range</div>
          </div>
          <div className="text-white text-sm lg:text-base font-semibold tracking-tight">
            {showAllDates
              ? "All Dates"
              : `${(fromDate && formatDateForAPI(fromDate)) || "N/A"} - ${(toDate && formatDateForAPI(toDate)) || "N/A"}`}
          </div>
        </div>
      </div>

      {/* Collapsible Filters */}
      <div className="bg-white/80 backdrop-blur-xl rounded-xl shadow-sm border border-gray-200/60 overflow-hidden mb-4 hover:shadow-lg transition-all duration-300">
        <div
          className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50 transition-all duration-300 group bg-white"
          onClick={() => setShowFilters(!showFilters)}
        >
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-sm group-hover:shadow-md transition-all duration-200">
              <FilterListIcon className="text-white w-4 h-4" />
            </div>
            <div className="flex flex-col">
              <span className="font-display font-semibold text-sm text-gray-900 tracking-tight">
                Filters & Actions
              </span>
              <span className="font-text text-xs text-gray-500">
                {showFilters
                  ? "Refine your search criteria"
                  : "Click to customize your view"}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="text-xs font-text text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
              {reportProducts.length} results
            </div>
            <IconButton
              size="small"
              className="text-gray-500 hover:text-blue-500 transition-all duration-200 hover:bg-blue-50 rounded-lg p-1"
            >
              {showFilters ? (
                <KeyboardArrowUpIcon className="w-4 h-4" />
              ) : (
                <KeyboardArrowDownIcon className="w-4 h-4" />
              )}
            </IconButton>
          </div>
        </div>

        <div
          className={`transition-all duration-500 ease-out overflow-hidden ${showFilters ? "max-h-[400px] opacity-100" : "max-h-0 opacity-0"
            }`}
        >
          <div className="border-t border-gray-100/60">
            <div className="px-3 pb-3 pt-2 max-h-[350px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
              <OrderFilters
                fromDate={fromDate}
                setFromDate={setFromDate}
                toDate={toDate}
                setToDate={setToDate}
                showAllDates={showAllDates}
                setShowAllDates={setShowAllDates}
                status={status}
                setStatus={setStatus}
                orderType={orderType}
                setOrderType={setOrderType}
                orderTypeOptions={[]}
                search={search}
                setSearch={setSearch}
                hideNonDateFilters={true}
              />
              <div className="mt-2">
                <OrderActions
                  onDownloadPDF={handleDownloadPDF}
                  hideSummary={true}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Product Table */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
        <div className="bg-gradient-to-r from-gray-900 to-black px-6 py-4 flex items-center justify-between border-b-2 border-white">
          <h3 className="text-white text-lg font-semibold">Product Report</h3>
        </div>

        {isLoading ? (
          <div className="px-6 py-16 text-center">
            <div className="animate-pulse flex flex-col items-center justify-center">
              <svg
                className="w-12 h-12 text-gray-300 mb-3"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"
                />
              </svg>
              <p className="text-gray-500 font-medium text-lg">
                Loading products...
              </p>
            </div>
          </div>
        ) : currentRows.length === 0 ? (
          <div className="px-6 py-16 text-center">
            <div className="text-gray-400 mb-3">
              <svg
                className="w-16 h-16 mx-auto"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"
                />
              </svg>
            </div>
            <p className="text-gray-500 text-lg font-medium">
              No products found
            </p>
            <p className="text-gray-400 text-sm mt-1">
              Try adjusting your date filters
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-gray-900 to-black sticky top-0 z-10">
                <tr>
                  <th
                    className={thClass}
                    onClick={() => handleSort("productName")}
                  >
                    <div className="flex items-center gap-2">
                      Product Name
                      <SortIndicator field="productName" />
                    </div>
                  </th>
                  <th
                    className={thClass}
                    onClick={() => handleSort("dineInQty")}
                  >
                    <div className="flex items-center gap-2">
                      Dine-in Qty
                      <SortIndicator field="dineInQty" />
                    </div>
                  </th>
                  {qrFeatureEnabled ? (
                    <th
                      className={thClass}
                      onClick={() => handleSort("qrQty")}
                    >
                      <div className="flex items-center gap-2">
                        QR Qty
                        <SortIndicator field="qrQty" />
                      </div>
                    </th>
                  ) : null}
                  <th
                    className={thClass}
                    onClick={() => handleSort("takeawayQty")}
                  >
                    <div className="flex items-center gap-2">
                      Takeaway Qty
                      <SortIndicator field="takeawayQty" />
                    </div>
                  </th>
                  <th
                    className={thClass}
                    onClick={() => handleSort("onlineQty")}
                  >
                    <div className="flex items-center gap-2">
                      Online Qty
                      <SortIndicator field="onlineQty" />
                    </div>
                  </th>
                  <th
                    className={thClass}
                    onClick={() => handleSort("totalQty")}
                  >
                    <div className="flex items-center gap-2">
                      Total Qty
                      <SortIndicator field="totalQty" />
                    </div>
                  </th>
                  <th
                    className={thClass}
                    onClick={() => handleSort("totalSales")}
                  >
                    <div className="flex items-center gap-2">
                      Sales
                      <SortIndicator field="totalSales" />
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {currentRows.map((product, index) => (
                  <tr
                    key={`${product.productName}-${index}`}
                    className="h-[70px] hover:bg-gradient-to-r hover:from-gray-50 hover:to-white transition-all duration-200 hover:shadow-sm"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="font-semibold text-gray-900 text-base">
                        {product.productName || "Unnamed"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-gray-700 text-sm font-medium">
                        {product.dineInQty ?? 0}
                      </span>
                    </td>
                    {qrFeatureEnabled ? (
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-gray-700 text-sm font-medium">
                          {product.qrQty ?? 0}
                        </span>
                      </td>
                    ) : null}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-gray-700 text-sm font-medium">
                        {product.takeawayQty ?? 0}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-gray-700 text-sm font-medium">
                        {product.onlineQty ?? 0}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="font-semibold text-blue-600 text-base">
                        {product.totalQty ?? 0}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="font-semibold text-green-600 text-base">
                        {currencySymbol}
                        {Number(product.totalSales || 0).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {reportProducts.length > TABLE_ROWS_PER_PAGE && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        )}
      </div>
    </div>
  );
};

export default ProductReport;
